<script lang="ts">
	import { format } from 'svelte-i18n';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { devices_config } from '@devices/index';
	import { devices, user_settings } from 'src/stores';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { getActivePvSetupType } from 'src/components/EnergyMonitoring/helpers/pv_flow_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import type { DashWidgetEditType, ListRefType } from '../../dash-types';
	import {
		DASH_ENERGY_INFO_CATEGORY_META,
		DASH_ENERGY_INFO_CATEGORY_ORDER,
		type DashEnergyInfoKpiId,
	} from '../../dash-energy-types';
	import DashWidgetLimitAlert from '../Components/DashWidgetLimitAlert.svelte';

	export let editItem: DashWidgetEditType;
	export let allItemsRefs: ListRefType = {};

	const MAX_INFO_CATEGORIES = 4;

	$: pvDevices = getPVSetupDevices(getActivePvSetupType());
	$: hasSolarMeter = pvDevices.solarDevices.length > 0;
	$: hasGridMeter = pvDevices.gridDevices.length > 0;
	$: hasPowerMeter = Object.values($devices).some((device) => devices_config[device?.type]?.isPM);
	$: hasTariffs = Boolean($user_settings?.power_prices || $user_settings?.power_prices_live_tariff);

	const isAvailable = (id: DashEnergyInfoKpiId): boolean => {
		if (id === 'solar' || id === 'periodGeneration') return hasSolarMeter;
		if (id === 'selfPowered') return hasSolarMeter && hasGridMeter;
		if (id === 'gridImport') return hasGridMeter;
		if (id === 'tariff' || id === 'currentTariff' || id === 'offPeak') return hasTariffs;
		if (
			id === 'periodConsumption' ||
			id === 'topConsumer' ||
			id === 'standby' ||
			id === 'peakDemand'
		) {
			return hasPowerMeter;
		}
		return true;
	};

	$: list = DASH_ENERGY_INFO_CATEGORY_ORDER.map((id) => {
		const meta = DASH_ENERGY_INFO_CATEGORY_META[id];
		return {
			id,
			name: $format(meta.labelKey, { default: meta.labelDefault }),
			description: $format(meta.descriptionKey, { default: meta.descriptionDefault }),
			icon: meta.icon,
			type: 'energy_info',
			disabled:
				!isSelected(id) &&
				(!isAvailable(id) || editItem.selectItems.length >= MAX_INFO_CATEGORIES),
		};
	});

	const isSelected = (id: DashEnergyInfoKpiId) =>
		editItem.selectItems.some((item) => String(item.id) === id);

	const handleSelectItem = (id: DashEnergyInfoKpiId) => {
		if (isSelected(id)) {
			editItem.selectItems = editItem.selectItems.filter((item) => String(item.id) !== id);
			return;
		}

		if (editItem.selectItems.length >= MAX_INFO_CATEGORIES) return;

		const nextPosition = editItem.selectItems.length
			? Math.max(...editItem.selectItems.map((item) => item.position ?? 0)) + 1
			: 0;

		editItem.selectItems = [
			...editItem.selectItems,
			{
				type: 'dashEnergyInfo',
				itemType: 'energy_info',
				id,
				position: nextPosition,
			},
		];
	};
</script>

<DashWidgetLimitAlert counter={editItem.selectItems.length} limit={MAX_INFO_CATEGORIES} />

<SelectList
	title={capFirstChar(
		$format('label_selected', {
			default: 'Selected:',
		}),
	)}
	titleHighlight={editItem.selectItems.length}
	{list}
	variant="checkbox"
	selectedCardsId={editItem.selectItems}
	bind:allCardsRefs={allItemsRefs}
	on:click={(e) => handleSelectItem(e.detail.id as DashEnergyInfoKpiId)}
/>
